import { useCallback } from 'react'

import { nanoid } from 'nanoid'
import { useNavigate } from 'react-router-dom'

import { generateChatPath } from '@bettermode/common/utils'
import {
  useSendMessageToChat,
  useSendMessageToMember,
} from '@tribeplatform/react-sdk/hooks'

import { getAttachmentsToSend } from './chat.context.utils.js'
import {
  HandleSendMessageToExistingChatParams,
  HandleSendMessageToNewChatParams,
} from './types.js'

interface Props {
  chatId: string
}

export const useSendMessage = ({ chatId }: Props) => {
  const navigate = useNavigate()
  const { mutateAsync: sendMessageToMember } = useSendMessageToMember()
  const { mutateAsync: sendMessage } = useSendMessageToChat({})

  const handleSendMessageToExistingChat = useCallback(
    async ({
      content,
      attachments,
      embeds,
      authorParticipantId,
    }: HandleSendMessageToExistingChatParams) => {
      const { attachments: attachmentsToSend, images: imagesToSend } =
        getAttachmentsToSend(attachments)
      const nonce = nanoid()

      await sendMessage({
        chatId,
        input: {
          content,
          attachments: attachmentsToSend,
          images: imagesToSend,
          embeds,
          nonce,
          authorParticipantId,
        },
      })
    },
    [chatId, sendMessage],
  )

  const handleSendMessageToNewChat = useCallback(
    async ({
      recipientId,
      content,
      attachments,
      embeds,
    }: HandleSendMessageToNewChatParams) => {
      if (!recipientId) {
        return
      }
      const { attachmentsIds, imagesIds } = getAttachmentsToSend(attachments)
      const embedIds = embeds.map(({ id }) => id)

      const { chatId } = await sendMessageToMember({
        input: {
          recipientId,
          message: {
            content,
            attachmentIds: attachmentsIds,
            imageIds: imagesIds,
            embedIds,
          },
        },
      })
      navigate(generateChatPath(chatId), { replace: true })
    },
    [navigate, sendMessageToMember],
  )

  return {
    handleSendMessageToExistingChat,
    handleSendMessageToNewChat,
  }
}
